// src/components/pages/portfolios/partials/PortfolioDetailAttribution.js

import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid, ReferenceLine
} from 'recharts';
import styles from './../style/PortfolioDetail.module.css';

const PERIODS = ['1w','2w','1m', '3m', '6m', '1y'];

// 탭 구성 및 색상 테마 정의 (라벨을 번역 키로 변경)
const ANALYSIS_MODES = [
  { key: 'return', labelKey: 'return', goodColor: '#4caf50', badColor: '#ff4d4d' },
  { key: 'risk', labelKey: 'risk', goodColor: '#2196f3', badColor: '#ff9800' }, // Good: 파랑(안정), Bad: 주황(불안)
  { key: 'defense', labelKey: 'defense', goodColor: '#2196f3', badColor: '#ff4d4d' }, // Good: 파랑(방어), Bad: 빨강(손실)
];

// 번역 키를 사용하도록 수정된 getInsightText
const getInsightText = (chartData, mode, currentModeInfo, t) => {
    if (!chartData || chartData.length === 0) return null;
    
    const firstItem = chartData[0];
    const lastItem = chartData[chartData.length - 1];

    if (mode === 'return') {
      return (
        <>
          <span style={{color: currentModeInfo.goodColor}}>▲ {firstItem.ticker}</span>
          {t('portfolioDetail.attributionInsight.returnBest')}
          <span style={{color: currentModeInfo.badColor, marginLeft: '6px'}}>▼ {lastItem.ticker}</span>
          {t('portfolioDetail.attributionInsight.returnWorst')}
        </>
      );
    } else if (mode === 'risk') {
      return (
        <>
          <span style={{color: currentModeInfo.badColor}}>▼ {firstItem.ticker}</span>
          {t('portfolioDetail.attributionInsight.riskWorst')}
          <span style={{color: currentModeInfo.goodColor, marginLeft: '6px'}}>▲ {lastItem.ticker}</span>
          {t('portfolioDetail.attributionInsight.riskBest')}
        </>
      );
    } else if (mode === 'defense') {
      return (
        <>
           <span style={{color: currentModeInfo.badColor}}>▼ {firstItem.ticker}</span>
           {t('portfolioDetail.attributionInsight.defenseWorst')}
           <span style={{color: currentModeInfo.goodColor, marginLeft: '6px'}}>▲ {lastItem.ticker}</span>
           {t('portfolioDetail.attributionInsight.defenseBest')}
        </>
      );
    }
    return null;
  };


export default function PortfolioDetailAttribution({ timeSeries, t }) {
  const [period, setPeriod] = useState('1w');
  const [mode, setMode] = useState('return'); // 'return' | 'risk' | 'defense'

  const attributionData = useMemo(() => {
    console.log('[attributionData]', timeSeries?.stockAttribution)
    return timeSeries?.stockAttribution || {};
    
  }, [timeSeries]);

  // 🔥 핵심 로직: Best 3와 Worst 3를 동시에 추출 (로직 변경 없음)
  const chartData = useMemo(() => {
    
    const data = attributionData[period];
    
    if (!data || data.length === 0) return [];

    let processedData = [];

    if (mode === 'return') {
      // 1. 수익률: 값이 클수록 Good, 작을수록(음수) Bad
      const sorted = [...data].sort((a, b) => b.contrib_return - a.contrib_return);
      
      const best3 = sorted.slice(0, 3).map(d => ({ ...d, value: d.contrib_return, type: 'Best' }));
      const worst3 = sorted.slice(-3).map(d => ({ ...d, value: d.contrib_return, type: 'Worst' }));
      
      // 중복 방지 (종목이 6개 미만일 경우)
      const combined = [...new Set([...best3, ...worst3])];
      // 차트에서는 '좋은거 -> 나쁜거' 순서로 정렬 유지
      processedData = combined.sort((a, b) => b.value - a.value);

    } else if (mode === 'risk') {
      // 2. 안정성(변동성): 값이 클수록 Bad(불안), 작을수록 Good(안정)
      const sorted = [...data].sort((a, b) => b.contrib_vol - a.contrib_vol);
      
      const worst3 = sorted.slice(0, 3).map(d => ({ ...d, value: d.contrib_vol, type: 'Worst' })); // 변동성 큼
      const best3 = sorted.slice(-3).map(d => ({ ...d, value: d.contrib_vol, type: 'Best' }));   // 변동성 작음
      
      const combined = [...new Set([...worst3, ...best3])];
      processedData = combined.sort((a, b) => b.value - a.value); // 차트엔 큰 값(Worst)이 위로 오게

    } else if (mode === 'defense') {
      // 3. 방어력(MDD): 값이 클수록(낙폭이 큼) Bad, 작을수록 Good
      const sorted = [...data].sort((a, b) => b.contrib_mdd - a.contrib_mdd);
      
      const worst3 = sorted.slice(0, 3).map(d => ({ ...d, value: d.contrib_mdd, type: 'Worst' })); // 낙폭 큼
      const best3 = sorted.slice(-3).map(d => ({ ...d, value: d.contrib_mdd, type: 'Best' }));   // 낙폭 작음
      
      const combined = [...new Set([...worst3, ...best3])];
      processedData = combined.sort((a, b) => b.value - a.value);
    }

    return processedData;
  }, [attributionData, period, mode]);

  const hasData = chartData.length > 0;
  const currentModeInfo = ANALYSIS_MODES.find(m => m.key === mode);

  return (
    <div className={styles.chartBlock}>
      <div className={styles.headerRow}>
        <div className={styles.titleContainer}>
          <h3 className={styles.subTitle}>
            {t('portfolioDetail.attributionTitle')}
          </h3>
        </div>

        <div className={styles.periodSelector}>
          {PERIODS.map(p => {
            const has = attributionData[p] && attributionData[p].length > 0;
            return (
              <button
                key={p}
                className={`${styles.periodButton} ${period === p ? styles.active : ''} ${!has ? styles.disabled : ''}`} 
                onClick={() => setPeriod(p)}
                disabled={!has}
              >
                {t(`portfolioDetail.periods.${p}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* 탭 버튼 */}
      <div className={styles.modeTabs} style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {ANALYSIS_MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              flex: '1 1 auto',
              padding: '8px 12px',
              borderRadius: '8px',
              border: mode === m.key ? `1px solid ${m.goodColor}` : '1px solid #444',
              background: mode === m.key ? `linear-gradient(90deg, ${m.goodColor}20, ${m.badColor}20)` : 'transparent',
              color: mode === m.key ? '#fff' : '#888',
              fontWeight: mode === m.key ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            {/* ✨ 수정: 라벨을 번역 키로 변경 */}
            {t(`portfolioDetail.analysisMode.${m.labelKey}`)}
          </button>
        ))}
      </div>

      {hasData ? (
        <div className={styles.attributionContainer}>
          {/* ✨ 수정: 인사이트 멘트 함수 호출 및 t() 전달 */}
          <div className={styles.attributionSummary} style={{marginBottom: '10px', fontSize: '14px', lineHeight: '1.5'}}>
            💬 {getInsightText(chartData, mode, currentModeInfo, t)}
          </div>

          <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 45)}>
            <BarChart 
              layout="vertical" 
              data={chartData} 
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#444" />
              <XAxis type="number" tickFormatter={(val) => `${(val * 100).toFixed(1)}%`} stroke="#aaa" />
              <YAxis dataKey="ticker" type="category" width={60} stroke="#fff" fontWeight="bold" />
              <Tooltip 
                cursor={{fill: 'rgba(255,255,255,0.05)'}}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const d = payload[0].payload;
                    
                    let valueColor = '#fff';
                    if (mode === 'return') valueColor = d.value > 0 ? currentModeInfo.goodColor : currentModeInfo.badColor;
                    else valueColor = d.type === 'Best' ? currentModeInfo.goodColor : currentModeInfo.badColor;

                    // ✨ 수정: 툴팁 내부 텍스트를 번역 키로 변경
                    const modeLabel = mode === 'return' ? t('portfolioDetail.attributionTooltip.return') : mode === 'risk' ? t('portfolioDetail.attributionTooltip.volatility') : t('portfolioDetail.attributionTooltip.mdd');

                    return (
                      <div className={styles.customTooltip} style={{backgroundColor: '#222', padding: '12px', border: '1px solid #555', borderRadius: '4px', boxShadow: '0 4px 10px rgba(0,0,0,0.5)'}}>
                        <p style={{fontWeight: 'bold', color: '#fff', marginBottom: '5px', borderBottom: '1px solid #444', paddingBottom: '5px'}}>
                          {d.ticker} <span style={{fontSize:'11px', color: '#888', fontWeight:'normal'}}>
                            ({d.type === 'Best' ? t('portfolioDetail.attributionTooltip.good') : t('portfolioDetail.attributionTooltip.bad')})
                          </span>
                        </p>
                        <div style={{fontSize: '12px', color: '#ccc', lineHeight: '1.6'}}>
                           <div>
                            {t('portfolioDetail.attributionTooltip.avgWeight')}: <span style={{color:'#fff'}}>{(d.avgWeight * 100).toFixed(1)}%</span>
                           </div>
                           <div>
                             {modeLabel}: 
                             <span style={{marginLeft:'4px', color: valueColor}}>
                               {( (mode==='return' ? d.return : mode==='risk' ? d.volatility : d.mdd) * 100).toFixed(1) }%
                             </span>
                           </div>
                           <div style={{marginTop:'4px', fontWeight:'bold'}}>
                             {t('portfolioDetail.attributionTooltip.contribution')}: <span style={{color: valueColor}}>{(d.value * 100).toFixed(2)}%</span>
                           </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <ReferenceLine x={0} stroke="#666" />
              <Bar dataKey="value" barSize={20} radius={[0, 3, 3, 0]}>
                {chartData.map((entry, index) => {
                  let color;
                  if (mode === 'return') {
                    // 수익률: 양수면 Good, 음수면 Bad
                    color = entry.value >= 0 ? currentModeInfo.goodColor : currentModeInfo.badColor;
                  } else {
                    // Risk/Defense: Best타입(값이 작음)은 GoodColor, Worst타입(값이 큼)은 BadColor
                    // 주의: 차트값(value) 자체는 양수이므로 방향은 오른쪽으로 동일함.
                    color = entry.type === 'Best' ? currentModeInfo.goodColor : currentModeInfo.badColor;
                  }
                  return <Cell key={`cell-${index}`} fill={color} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className={styles.noDataMessage}>
           {/* noPeriodData 키는 PortfolioDetail.js 인접 파일에서 이미 추가되었습니다. */}
           {t('portfolioDetail.noPeriodData', { period: t(`portfolioDetail.periods.${period}`) })}
        </div>
      )}
    </div>
  );
}